import React, { useEffect, useRef, useState } from 'react';
import { studioService, getPublishedState, type PublishedState } from '../../services/studio.service';
import { BrandBtn, DangerNote, Icons, PUBLISH_COST, Spinner, fmtCredits } from './studioUi';

interface PublishDialogProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    onPublish: (subdomain: string) => Promise<{ success: boolean; url?: string; error?: string }>;
    credits: number;
    /** Needed to look up the current publish state and to allow reusing this app's own domain. */
    applicationId: string;
    /** Opens the top-up dialog when the balance is short. */
    onTopUp: () => void;
    /** Fired after a successful publish so the shell can refresh its published state. */
    onPublished?: () => void;
    /** Opens the custom-domain dialog (connect or buy a .com / .id). */
    onOpenDomains?: () => void;
}

type SlugStatus = 'idle' | 'short' | 'checking' | 'free' | 'taken';

/**
 * "Publish your site" from the handoff design. Two stages: the subdomain form
 * (with the republish choice for apps that are already live) and "Your site is
 * live" with the URL the SERVER built — never reconstructed here, since
 * published apps resolve by host at the Caddy edge.
 */
export const PublishDialog: React.FC<PublishDialogProps> = ({ open, onOpenChange, onPublish, credits, applicationId, onTopUp, onPublished, onOpenDomains }) => {
    const [slug, setSlug] = useState('');
    const [status, setStatus] = useState<SlugStatus>('idle');
    // Real published-app domain, supplied by check-subdomain. 'hyvay.com' only fills
    // the first render, before the first availability check answers.
    const [appDomain, setAppDomain] = useState('hyvay.com');
    const [existing, setExisting] = useState<PublishedState | null>(null);
    const [mode, setMode] = useState<'reuse' | 'new'>('new');
    const [publishing, setPublishing] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [liveUrl, setLiveUrl] = useState<string | null>(null);
    const [copied, setCopied] = useState(false);
    const checkTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
    const copyTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

    const close = () => { if (!publishing) onOpenChange(false); };

    // On open: reset, then discover whether this app is already live (default: reuse).
    useEffect(() => {
        if (!open) return;
        setSlug(''); setStatus('idle'); setMode('new'); setPublishing(false); setError(null); setLiveUrl(null); setCopied(false);
        let cancelled = false;
        void (async () => {
            const state = await getPublishedState(applicationId);
            if (cancelled) return;
            setExisting(state);
            if (state) {
                setMode('reuse');
                setSlug(state.subdomain);
                setStatus('free'); // its own address is by definition usable
                const host = state.url.replace(/^https?:\/\//, '').split('/')[0].split(':')[0];
                const dom = host.startsWith(`${state.subdomain}.`) ? host.slice(state.subdomain.length + 1) : null;
                if (dom) setAppDomain(dom);
            }
        })();
        return () => { cancelled = true; };
    }, [open, applicationId]);

    useEffect(() => () => {
        if (checkTimer.current) clearTimeout(checkTimer.current);
        if (copyTimer.current) clearTimeout(copyTimer.current);
    }, []);

    const onSlug = (raw: string) => {
        const next = raw.toLowerCase().replace(/[^a-z0-9-]/g, '-').replace(/-{2,}/g, '-');
        setSlug(next);
        setError(null);
        if (checkTimer.current) clearTimeout(checkTimer.current);
        if (!next.trim()) { setStatus('idle'); return; }
        if (next.length < 3) { setStatus('short'); return; }
        setStatus('checking');
        checkTimer.current = setTimeout(async () => {
            const { available, domain } = await studioService.checkSubdomainAvailability(next, applicationId);
            if (domain) setAppDomain(domain);
            setStatus(available ? 'free' : 'taken');
        }, 600);
    };

    const canAfford = credits >= PUBLISH_COST;
    const reusing = !!existing && mode === 'reuse';
    const ready = status === 'free' && !!slug.trim() && canAfford && !publishing;

    const confirm = async () => {
        if (!ready) return;
        setPublishing(true);
        setError(null);
        const result = await onPublish(slug);
        setPublishing(false);
        if (result.success && result.url) {
            setLiveUrl(result.url);
            onPublished?.();
        } else if (result.success) {
            setError('The site was published, but the server did not return its URL.');
            onPublished?.();
        } else {
            setError(result.error || 'Publishing failed. Please try again.');
        }
    };

    const copyLive = async () => {
        if (!liveUrl) return;
        try { await navigator.clipboard.writeText(liveUrl); } catch { /* ignore */ }
        setCopied(true);
        if (copyTimer.current) clearTimeout(copyTimer.current);
        copyTimer.current = setTimeout(() => setCopied(false), 1800);
    };

    const slugBorder = status === 'taken' ? 'var(--hv-danger)' : status === 'free' && slug ? 'var(--hv-leaf)' : 'var(--hv-line-2)';
    const slugMsg: Record<SlugStatus, string> = {
        idle: 'Pick a subdomain to continue.',
        short: 'Use at least 3 characters.',
        checking: 'Checking availability…',
        free: reusing ? 'This is the address your site uses today.' : 'Subdomain is available.',
        taken: 'That subdomain is taken. Try another one.',
    };
    const slugMsgFg = status === 'taken' ? 'var(--hv-danger)' : status === 'free' && slug ? 'var(--hv-leaf)' : 'var(--hv-muted)';
    const preview = `${slug.trim() || 'your-site-name'}.${appDomain}`;

    const optionRow = (active: boolean, onPick: () => void, title: string, sub: string) => (
        <button type="button" onClick={onPick} className={active ? '' : 'hv-h-page2-bd'} style={{ display: 'flex', alignItems: 'flex-start', gap: 11, width: '100%', padding: '10px 12px', borderRadius: 8, textAlign: 'left', cursor: 'pointer', border: `1px solid ${active ? 'var(--brand,#13422C)' : 'var(--hv-line)'}`, background: active ? 'var(--hv-mint)' : 'transparent', color: active ? 'var(--hv-mint-ink)' : 'var(--hv-ink)' }}>
            <span style={{ flex: 'none', marginTop: 2, width: 15, height: 15, borderRadius: 999, border: `1.5px solid ${active ? 'var(--brand,#13422C)' : 'var(--hv-line-2)'}`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                {active && <span style={{ width: 7, height: 7, borderRadius: 999, background: 'var(--brand,#13422C)' }} />}
            </span>
            <span style={{ flex: 1, minWidth: 0 }}>
                <span style={{ display: 'block', fontSize: 13.5, fontWeight: 600 }}>{title}</span>
                <span style={{ display: 'block', marginTop: 2, fontSize: 12, lineHeight: 1.5, color: active ? 'var(--hv-mint-ink)' : 'var(--hv-body)' }}>{sub}</span>
            </span>
        </button>
    );

    const head = (title: string) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '11px 20px', background: 'var(--hv-page-2)', borderBottom: '1px solid var(--hv-line)' }}>
            <div id="hv-pub-title" style={{ flex: 1, minWidth: 0, fontSize: 14.5, fontWeight: 600, letterSpacing: '-0.01em' }}>{title}</div>
            <button type="button" onClick={close} aria-label="Close" className="hv-h-card-ink" style={{ flex: 'none', width: 30, height: 30, margin: '0 -6px 0 0', borderRadius: 999, border: 0, background: 'transparent', color: 'var(--hv-muted)', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round"><path d="M6 6l12 12M18 6L6 18" /></svg>
            </button>
        </div>
    );
    const fieldLabel: React.CSSProperties = { fontSize: 12, color: 'var(--hv-body)' };
    const ghost: React.CSSProperties = { height: 34, padding: '0 14px', borderRadius: 999, border: 0, background: 'transparent', fontSize: 13.5, fontWeight: 400, color: 'var(--hv-ink)', cursor: 'pointer', whiteSpace: 'nowrap' };
    const brand: React.CSSProperties = { flex: 'none', whiteSpace: 'nowrap', height: 34, padding: '0 14px', fontSize: 13.5, fontWeight: 600 };
    const note = (children: React.ReactNode) => (
        <DangerNote style={{ marginTop: 10, padding: '12px 14px', borderRadius: 8 }}>{children}</DangerNote>
    );

    return (
        <DialogShell open={open} onClose={close}>
            {liveUrl ? (
                <>
                    {head('Your site is live')}
                    <div style={{ padding: '16px 20px 18px' }}>
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', padding: '8px 0 18px' }}>
                            <span style={{ width: 52, height: 52, borderRadius: 999, background: 'var(--hv-mint)', color: 'var(--hv-mint-ink)', display: 'flex', alignItems: 'center', justifyContent: 'center', animation: 'hvRise 0.45s cubic-bezier(0.2,0.9,0.24,1) both' }}>
                                {Icons.check({ size: 24, sw: 2.6 })}
                            </span>
                            <p style={{ margin: '12px 0 0', fontSize: 13.5, lineHeight: 1.6, color: 'var(--hv-ink)' }}>Anyone with the link can open your site now.</p>
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                            <span style={fieldLabel}>Live URL</span>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8, height: 36, padding: '0 4px 0 12px', borderRadius: 6, border: '1px solid var(--hv-line-2)', background: 'var(--hv-card)' }}>
                                <a href={liveUrl} target="_blank" rel="noopener noreferrer" className="hv-h-underline" style={{ flex: 1, minWidth: 0, fontSize: 13.5, color: 'var(--hv-brand-ink)', textDecoration: 'none', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{liveUrl}</a>
                                <button type="button" onClick={() => void copyLive()} className="hv-h-page2" style={{ flex: 'none', display: 'flex', alignItems: 'center', gap: 6, height: 28, padding: '0 10px', borderRadius: 5, border: 0, background: 'transparent', color: 'var(--hv-ink)', fontSize: 12.5, fontWeight: 400, cursor: 'pointer', whiteSpace: 'nowrap' }}>
                                    {copied ? Icons.check({ size: 13, sw: 2.6 }) : Icons.copy({})}
                                    {copied ? 'Copied' : 'Copy'}
                                </button>
                            </div>
                        </div>
                        <div style={{ marginTop: 18, display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
                            <button type="button" onClick={() => onOpenChange(false)} className="hv-h-page2" style={ghost}>Close</button>
                            <BrandBtn style={brand} onClick={() => window.open(liveUrl, '_blank', 'noopener,noreferrer')}>View site</BrandBtn>
                        </div>
                    </div>
                </>
            ) : (
                <>
                    {head(existing ? 'Republish your site' : 'Publish your site')}
                    <div style={{ padding: '16px 20px 18px' }}>
                        <p style={{ margin: '0 0 14px', fontSize: 13.5, lineHeight: 1.6, color: 'var(--hv-ink)' }}>
                            {existing ? 'Rebuild and redeploy the latest version of your site.' : 'Pick the web address people will use to open your site.'}
                        </p>

                        {existing && (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 14 }}>
                                <span style={fieldLabel}>Address</span>
                                {optionRow(mode === 'reuse', () => { setMode('reuse'); setSlug(existing.subdomain); setStatus('free'); setError(null); }, 'Keep the same address', `${existing.subdomain}.${appDomain} · version ${existing.version}`)}
                                {optionRow(mode === 'new', () => { setMode('new'); setSlug(''); setStatus('idle'); setError(null); }, 'Use a new address', `The old address (${existing.subdomain}.${appDomain}) will stop working.`)}
                            </div>
                        )}

                        <label style={{ display: 'flex', flexDirection: 'column', gap: 6, opacity: reusing ? 0.6 : 1 }}>
                            <span style={fieldLabel}>Subdomain</span>
                            <span className="hv-composer" style={{ display: 'flex', alignItems: 'center', gap: 8, height: 36, padding: '0 0 0 12px', borderRadius: 6, background: 'var(--hv-card)', border: `1px solid ${slugBorder}`, transition: 'border-color 0.18s ease, box-shadow 0.18s ease' }}>
                                <input
                                    value={slug}
                                    onChange={(e) => onSlug(e.target.value)}
                                    placeholder="your-site-name"
                                    spellCheck={false}
                                    disabled={reusing}
                                    style={{ flex: 1, minWidth: 0, border: 0, outline: 'none', background: 'transparent', color: 'var(--hv-ink)', fontFamily: 'inherit', fontSize: 13.5 }}
                                />
                                {status === 'checking' && <Spinner size={16} />}
                                {status === 'free' && !!slug && <span style={{ flex: 'none', color: 'var(--hv-leaf)', display: 'flex' }}>{Icons.check({ size: 17, sw: 2.6 })}</span>}
                                {status === 'taken' && <span style={{ flex: 'none', color: 'var(--hv-danger)', display: 'flex' }}>{Icons.x({})}</span>}
                                <span style={{ flex: 'none', alignSelf: 'stretch', display: 'flex', alignItems: 'center', padding: '0 12px', borderLeft: '1px solid var(--hv-line)', background: 'var(--hv-page-2)', borderRadius: '0 5px 5px 0', fontSize: 13, color: 'var(--hv-muted)' }}>.{appDomain}</span>
                            </span>
                            <span style={{ fontSize: 12, color: slugMsgFg }}>{slugMsg[status]}</span>
                        </label>

                        {onOpenDomains && (
                            <div style={{ marginTop: 14, display: 'flex', alignItems: 'center', gap: 12, padding: '10px 12px', borderRadius: 8, border: '1px dashed var(--hv-line-2)', background: 'var(--hv-card-2)' }}>
                                <span style={{ flex: 'none', display: 'flex', color: 'var(--hv-mint-ink)' }}>{Icons.globe({ size: 17 })}</span>
                                <div style={{ flex: 1, minWidth: 0 }}>
                                    <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--hv-ink)' }}>Custom domain</div>
                                    <div style={{ marginTop: 2, fontSize: 12, lineHeight: 1.5, color: 'var(--hv-body)' }}>
                                        {existing?.customDomain
                                            ? <>Live at <span style={{ color: 'var(--hv-ink)', fontWeight: 600 }}>{existing.customDomain}</span> as well.</>
                                            : 'Use your own .com or .id, or buy one here.'}
                                    </div>
                                </div>
                                <button type="button" data-track="publish_domains" onClick={onOpenDomains} className="hv-h-mint-bd" style={{ flex: 'none', display: 'flex', alignItems: 'center', gap: 6, height: 30, padding: '0 12px', borderRadius: 999, border: '1px solid var(--hv-line-2)', background: 'transparent', color: 'var(--hv-body)', fontSize: 12.5, fontWeight: 600, cursor: 'pointer' }}>
                                    {existing?.customDomain ? 'Manage' : 'Set up'}
                                </button>
                            </div>
                        )}

                        <div style={{ marginTop: 16, display: 'flex', gap: 12, padding: '12px 14px', borderRadius: 8, background: canAfford ? 'var(--hv-mint)' : 'var(--hv-card-2)', color: canAfford ? 'var(--hv-mint-ink)' : 'var(--hv-body)' }}>
                            <span style={{ flex: 'none', marginTop: 1, display: 'flex' }}>{Icons.globe({})}</span>
                            <div style={{ flex: 1, minWidth: 0 }}>
                                <div style={{ fontSize: 13.5, fontWeight: 600 }}>{existing ? 'Publish the latest version' : 'Publish it live'}</div>
                                <div style={{ marginTop: 5, fontSize: 12.5, lineHeight: 1.6 }}>
                                    Your site will be open to anyone at <strong style={{ fontWeight: 700 }}>{preview}</strong>. Publishing costs {PUBLISH_COST} credits. You have {fmtCredits(credits)} credits.
                                </div>
                            </div>
                        </div>

                        {!canAfford && note(<>Not enough credits. You need {fmtCredits(PUBLISH_COST - credits)} more to publish.</>)}
                        {error && note(error)}

                        <div style={{ marginTop: 18, display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
                            <button type="button" onClick={close} disabled={publishing} className="hv-h-page2" style={{ ...ghost, opacity: publishing ? 0.5 : 1, cursor: publishing ? 'not-allowed' : 'pointer' }}>Cancel</button>
                            {canAfford ? (
                                <BrandBtn style={brand} disabled={!ready} data-track="publish_confirm" onClick={() => void confirm()}>
                                    {publishing ? 'Publishing…' : existing ? 'Republish' : 'Publish'}
                                    {publishing && <Spinner size={15} light />}
                                </BrandBtn>
                            ) : (
                                <BrandBtn style={brand} data-track="publish_top_up" onClick={() => { onOpenChange(false); onTopUp(); }}>
                                    Top up credits {Icons.arrow({})}
                                </BrandBtn>
                            )}
                        </div>
                    </div>
                </>
            )}
        </DialogShell>
    );
};

/** The design's compact publish dialog: top-aligned 440px card over a light scrim. */
const DialogShell: React.FC<{ open: boolean; onClose: () => void; children: React.ReactNode }> = ({ open, onClose, children }) => {
    useEffect(() => {
        if (!open) return;
        const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [open, onClose]);
    if (!open) return null;
    return (
        <div onClick={onClose} style={{ position: 'fixed', inset: 0, zIndex: 100, background: 'rgba(14,20,17,0.42)', display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: '80px 20px 20px' }}>
            <div
                role="dialog"
                aria-modal="true"
                aria-labelledby="hv-pub-title"
                onClick={(e) => e.stopPropagation()}
                style={{ width: '100%', maxWidth: 440, maxHeight: 'calc(100vh - 100px)', overflow: 'auto', borderRadius: 8, background: 'var(--hv-card)', border: '1px solid var(--hv-line)', boxShadow: '0 30px 60px -24px rgba(0,0,0,0.35)', color: 'var(--hv-ink)', animation: 'hvRise 0.25s cubic-bezier(0.2,0.9,0.24,1) both' }}
            >
                {children}
            </div>
        </div>
    );
};
